<template>
  <OrderSummarySlider />
  <lockRate />

  <div class="ecom inner-wrapper">
    <router-view />
    <div class="right-column">
      <div class="right-column__sticky">
        <div class="ecom summary-wrapper">
          <OrderSummary ref="orderSummary" class="desktop" />
        </div>
        <!-- Bound iframe src only, no v-html. Hidden when embed URL is empty. -->
        <div v-if="path === '/cart' && !item && brand === 'BD' && promoVideoSrc" class="promo-video mb-2">
          <div class="promo-video__player">
            <iframe
              class="promo-video__iframe"
              :src="promoVideoSrc"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            />
          </div>
        </div>
        <div v-if="!item && brand !== 'BD'" class="ecom instructional-wrapper">
          <InstructionalCard />
        </div>
        <Trustpilot />
        <div v-if="path === '/cart'" class="request-project-quote mt-2">
          <div class="request-project-quote__avatar">
            <img :src="contractorIcon" alt="Request Project Quote Contractor">
          </div>
          <div class="request-project-quote__copy" v-html="requestProjectQuote"></div>
        </div>
        <PriceLock v-if="path === '/cart' && item" />
        <ServiceFomo v-if="path === '/cart/service'" />
      </div>
    </div>
  </div>
  <hr v-if="path === '/cart' || path ==='/cart/service'" class='footer__divider'>
  <!-- Accepted Materials and Prohibited Items -->
  <div v-if="path === '/cart'" class="footer">
    <AcceptedMaterials />
    <ProhibitedItems />
  </div>
  <!-- Session Timeout Modal -->
  <modal class="session-timeout-modal" :isOpen="showSessionTimeoutModal" :showClose="false">
    <template v-slot:content>
      <div>
        <img class="session-timeout-modal__img" :src="themeLogo" alt="Brand logo" />
        <h3 class="session-timeout-modal__heading mt-1">Your session is about to time out</h3>
        <p class="mb-1" >Please choose an option below:</p>
        <div class="session-timeout-modal__cta">
          <button v-on:click="endSessionTimeoutModal" class="btn btn--tertiary session-timeout-modal__close mt-1">
            End Session
          </button>
            <button v-on:click="continueSessionTimeoutModal" class="btn btn--primary session-timeout-modal__close mt-1">
            Continue
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>
<script>

import OrderSummary from '@/components/OrderSummary.vue'
import Trustpilot from '@/components/Trustpilot.vue'
import InstructionalCard from '@/components/InstructionalCard.vue'
import OrderSummarySlider from '@/components/OrderSummarySlider.vue'
import AcceptedMaterials from '@/components/AcceptedMaterials.vue'
import ProhibitedItems from '../components/ProhibitedItems.vue'
import PriceLock from '@/components/PriceLock.vue'
import ServiceFomo from '@/components/ServiceFomo.vue'
import config from '@/config'
import Modal from '@/components/Modal.vue'
import LockRate from '@/components/LockRateModal.vue'
import { getAbandonedDNIS, getQueryString, getYouTubeEmbedSrc } from '@/utilities.js'
import { attributionQueryParam } from '@/utilities/constants'
import { mapState, mapActions } from 'vuex'

const { cdn, requestProjectQuote, ecomPromoVideo, links: { multipleDumpsters, commercialQuote } } = config

export default {
  data () {
    return {
      path: '/cart',
      isModalOpen: false,
      brand: process.env.VUE_APP_BRAND,
      requestProjectQuote,
      multipleDumpsters,
      commercialQuote,
      userActivityTimeout: null,
      optionsTimeout: null,
      showSessionTimeoutModal: false,
      themeLogo: `${cdn}theme-logo.png`,
      contractorIcon: `${cdn}contractor-icon.svg`
    }
  },
  components: {
    OrderSummary,
    InstructionalCard,
    OrderSummarySlider,
    AcceptedMaterials,
    ProhibitedItems,
    PriceLock,
    ServiceFomo,
    Trustpilot,
    Modal,
    LockRate
  },
  beforeRouteUpdate (to, from, next) {
    this.path = to.path
    next()
  },
  beforeMount () {
    // Entry only: read discount_code like other one-shot URL params (e.g. utm in AddressSearch). Store + applyDiscountCode own truth after this.
    this.extractDiscountFromUrl(this.$route)
  },
  mounted () {
    this.path = this.$route.path
    this.persistAttributionParam()
    window.addEventListener('mousemove', this.resetUserActivityTimeout)
    window.addEventListener('touchstart', this.resetUserActivityTimeout)
    window.addEventListener('scroll', this.resetUserActivityTimeout)
    window.addEventListener('keydown', this.resetUserActivityTimeout)
    window.addEventListener('resize', this.resetUserActivityTimeout)
    this.resetUserActivityTimeout()
  },
  methods: {
    ...mapActions('cart', ['createLead', 'updateLead', 'applyDiscountCode']),
    persistAttributionParam () {
      const attributionFromUrl = (getQueryString(attributionQueryParam) || '').toLowerCase()
      if (attributionFromUrl) {
        this.$store.commit('customer/attribution', attributionFromUrl)
      } else {
        this.$store.commit('customer/attribution', '')
      }
    },
    extractDiscountFromUrl (route) {
      const code = route.query?.discount_code
      if (!code || !String(code).trim()) return
      this.applyDiscountCode({ code: String(code).trim() })
    },
    endSessionTimeoutModal () {
      const sfid = this.ppcLeadUUID
      if (sfid || (this.$route.path === '/cart/payment' && !this.abandonedLeadSent)) {
        const { utmSource, attribution } = this.$store.state.customer
        const DNIS = getAbandonedDNIS(utmSource, attribution)
        this.$store.commit('customer/DNIS', DNIS)
        const abandonedPayloadAdditions = {
          abandonedCartFlag: true,
          DNIS
        }
        // if this is a PPC lead, update the current SF ID to abandoned, otherwise, create a SF lead for a non PPC abandoned
        if (sfid) {
          this.updateLead(abandonedPayloadAdditions)
        } else {
          this.createLead(abandonedPayloadAdditions)
        }
      }
      // if they choose to end their session, abandoned their experience, we don't want to reload the page, but send them to the standard cart without the ppc parameters, because that would create another ppc blank lead on reload
      location.href = '/cart'
      this.showSessionTimeoutModal = false
    },
    continueSessionTimeoutModal () {
      this.resetUserActivityTimeout()
      this.showSessionTimeoutModal = false
      clearTimeout(this.optionsTimeout)
    },
    resetUserActivityTimeout () {
      clearTimeout(this.userActivityTimeout)
      // set a timeout for 15 mins to show the session timeout dialog
      this.userActivityTimeout = setTimeout(() => {
        this.showSessionTimeoutModal = true
        // if the user doesn't select an option, end session or continue within 2 mins, auto end their session
        this.optionsTimeout = setTimeout(() => {
          location.reload()
        }, 120000)
      }, 900000) // 15 mins
    },
    closeModal () {
      this.isModalOpen = false
    }
  },
  computed: {
    ...mapState('cart', [
      'abandonedLeadSent',
      'item',
      'ppcLeadUUID'
    ]),
    ...mapState('customer', ['serviceAddress']),
    promoVideoSrc () {
      // Validated embed URL from config; empty string if the watch URL is invalid.
      return getYouTubeEmbedSrc(ecomPromoVideo)
    }
  }
}

</script>
// Do not remove - this has to be here so webpack will hot reload CSS
<style lang="scss">
// body {
//   color: red;
// }
</style>
